import { useApolloClient } from '@apollo/client';
import { message, Form } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouterSpecsFragment } from '../../../../api/fragments';
import { type BasicProLayoutSettings, type BasicRouterSettings } from '../../../../api/types';
import useHandleError from '../../../../utilities/useHandleError';
import { getInitialValues, implementations } from './layouts';

export type LayoutFormProps = {
    router: RouterSpecsFragment;
    closeDrawer: () => void;
};

type FormValues =
    | {
          type: 'BasicProLayout';
          settings: BasicProLayoutSettings;
      }
    | {
          type: 'BasicLayout';
          settings: BasicRouterSettings;
      };

const LayoutForm = ({ router, closeDrawer }: LayoutFormProps) => {
    const { id: routerId, layout } = router;

    const apolloClient = useApolloClient();
    const { t } = useTranslation(['routerDetails']);

    const initialValues = useMemo(
        () =>
            layout
                ? { type: layout.__typename, settings: getInitialValues(layout) }
                : { type: 'BasicProLayout', settings: implementations.BasicProLayout.defaultSettings },
        [layout]
    );

    const onSubmit = useHandleError<FormValues>(
        async values => {
            message.loading({
                content: t('routerDetails:layouts.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            const promise = implementations[values.type].applyRouter(apolloClient, routerId, values.settings as any);

            return promise
                .finally(() => {
                    message.destroy('primary');
                })
                .then(() => {
                    // inform about the success
                    message.success({
                        content: t('routerDetails:layouts.messages.success'),
                        key: 'primary',
                    });

                    // then close drawer
                    closeDrawer();
                });
        },
        [routerId, apolloClient, t, closeDrawer]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            <UpdateRouterLayoutFormInner />
        </Formik>
    );
};

const UpdateRouterLayoutFormInner = () => {
    const { values, handleSubmit } = useFormikContext<FormValues>();

    const { FormComponent } = implementations[values.type];

    return (
        <Form id="applyRouterLayoutForm" layout="vertical" onSubmitCapture={handleSubmit}>
            <FormComponent />
        </Form>
    );
};

export default LayoutForm;
